
import React, { useEffect, useState } from "react";
import { useParams, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { NursingHome } from "@/hooks/use-nursing-home";
import { OwnerPageLoadingState } from "@/components/nursing-homes/owner/LoadingState";
import { UnauthorizedState } from "@/components/nursing-homes/owner/UnauthorizedState";
import { OwnerPageErrorState } from "@/components/nursing-homes/owner/ErrorState";
import { SidebarNav } from "@/components/nursing-homes/owner/dashboard/SidebarNav";
import { SidebarProvider } from "@/components/ui/sidebar";

export default function OwnerNursingHomePage() {
  const { nhSlug } = useParams();
  const { user, isLoading: authLoading } = useAuth();
  const [nursingHome, setNursingHome] = useState<NursingHome | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const checkOwnershipAndLoadData = async () => {
      if (!user || !nhSlug) return;
      
      try {
        setIsLoading(true);
        
        // Check if user has an approved claim for this nursing home
        const { data: claims, error: claimsError } = await supabase
          .from('nursing_home_claims')
          .select('*')
          .eq('user_id', user.id)
          .eq('nursing_home_id', nhSlug)
          .eq('status', 'approved')
          .limit(1);
        
        if (claimsError) throw claimsError;
        
        if (!claims || claims.length === 0) {
          setIsAuthorized(false);
          return;
        }
        
        setIsAuthorized(true);
        
        // Fetch nursing home details
        const { data: home, error: homeError } = await supabase
          .from('nursing_homes')
          .select('*')
          .eq('nh_slug', nhSlug)
          .single();
        
        if (homeError) throw homeError;
        
        setNursingHome(home);
      } catch (err) {
        console.error("Error fetching nursing home data:", err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        toast.error("Failed to load nursing home data");
      } finally {
        setIsLoading(false);
      }
    };

    if (user && !authLoading && nhSlug) {
      checkOwnershipAndLoadData();
    }
    
    // Set active tab based on URL path
    const path = window.location.pathname;
    if (path.includes("/profile")) setActiveTab("profile");
    else if (path.includes("/media")) setActiveTab("media");
    else if (path.includes("/leads")) setActiveTab("leads");
    else if (path.includes("/staff")) setActiveTab("staff");
    else if (path.includes("/calendar")) setActiveTab("calendar");
    else if (path.includes("/settings")) setActiveTab("settings");
    else setActiveTab("overview");
    
  }, [user, authLoading, nhSlug]);

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to={`/auth/login?redirectTo=/owner/nursing-homes/${nhSlug}`} />;
  }

  if (isLoading) {
    return <OwnerPageLoadingState />;
  }

  if (!isAuthorized) {
    return <UnauthorizedState />;
  }

  if (error || !nursingHome) {
    return <OwnerPageErrorState error={error} />;
  }

  return (
    <Layout>
      <Helmet>
        <title>{nursingHome.center_name} - Owner Management | Grandpal</title>
      </Helmet>

      <div className="container p-0 max-w-7xl">
        <SidebarProvider>
          <div className="flex min-h-[calc(100vh-80px)] w-full">
            <SidebarNav activeTab={activeTab} nursingHomeSlug={nhSlug} />
            <div className="flex-1 p-6">
              <Outlet context={{ nursingHome }} />
            </div>
          </div>
        </SidebarProvider>
      </div>
    </Layout>
  );
}
